import track, { useTracking } from 'react-tracking';
import { NewScreener, SavedScreenerSpinnerContainer } from './styled';
import {
  Heading,
  Box,
  Text,
  Grid,
  Bones,
  useTheme,
  Button,
  LoadingSpinner,
} from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import If from '@/components/If';
import type { UserView } from '../../Screener';

import { ROUTE_CREATE_CRITERIA } from '@/constants/routes';

import SavedScreenersSlider from './components/SavedScreenersSlider';
import FeedbackAlert from '../FeedbackAlert';

interface Props {
  name: string;
  description: string;
  canSaveScreener: boolean;
  isEditingUserSavedScreener: boolean;
  selectedView: string;
  userIdeas: UserView[];
  userId: string;
  isLoadingUserViews: boolean;
  isUpdatingUserViews: boolean;
  isLoadingUserView: boolean;
  onEditNameClick: () => void;
  onCreateNavigate: () => void;
  onUserViewChange: (id: string, name: string) => void;
}

const HeaderExperiment = ({
  description,
  isEditingUserSavedScreener,
  name,
  selectedView,
  userIdeas,
  canSaveScreener,
  userId,
  isLoadingUserViews,
  isUpdatingUserViews,
  isLoadingUserView,
  onEditNameClick,
  onUserViewChange,
  onCreateNavigate,
}: Props) => {
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const showScreenerNavigation = Boolean(
    canSaveScreener && (isLoadingUserViews || userIdeas.length)
  );

  return (
    <Box padding="x2" paddingLg="x0" paddingBottomLg="x2">
      <Grid templateColumns="auto max-content" gap="4px" alignItems="center">
        <Heading as="h1" typography="heroStrong">
          {isEditingUserSavedScreener
            ? 'Stock Screener'
            : 'Create a Stock Screener'}
        </Heading>

        {isEditingUserSavedScreener ? (
          <NewScreener
            styleType="gentle"
            to={{ type: ROUTE_CREATE_CRITERIA }}
            onClick={() => {
              onCreateNavigate();
              trackEvent({
                action: 'click',
                subject: 'new-screener',
              });
            }}
          >
            New Screener
          </NewScreener>
        ) : (
          <Button styleType="gentle" disabled>
            New Screener
          </Button>
        )}
      </Grid>

      <Box marginTop="x3" marginBottom="x2">
        <FeedbackAlert userId={userId} />
      </Box>

      {showScreenerNavigation && (
        <Grid
          templateColumnsMd="max-content 1fr"
          alignItems="center"
          gap="8px"
          maxWidth="100%"
        >
          <Grid
            templateColumns="max-content max-content"
            alignItems="center"
            gap="8px"
          >
            <Text color="mediumGray" typography="small">
              Saved screeners
            </Text>
            <SavedScreenerSpinnerContainer
              isLoading={!isLoadingUserViews && isUpdatingUserViews}
            >
              <LoadingSpinner light />
            </SavedScreenerSpinnerContainer>
          </Grid>

          {isLoadingUserViews ? (
            <Grid
              templateColumns="repeat(3, minmax(auto, 150px))"
              templateRows="70px"
              gap="8px"
            >
              <Bones />
              <Bones />
              <Bones />
            </Grid>
          ) : (
            <SavedScreenersSlider
              selectedView={selectedView}
              userViews={userIdeas}
              onUserViewChange={onUserViewChange}
            />
          )}
        </Grid>
      )}

      <If
        conditions={{
          notEditing: !isEditingUserSavedScreener,
          loading: isEditingUserSavedScreener && isLoadingUserView,
          loaded: isEditingUserSavedScreener && !isLoadingUserView,
        }}
      >
        {{
          notEditing: null,
          loading: (
            <Box marginTop="x2">
              <Bones minHeight={theme.x4} />
              <Bones minHeight={theme.x2} marginTop="x2" />
            </Box>
          ),
          loaded: (
            <Box marginTop="x2">
              <Grid
                templateColumns="minmax(0, max-content) max-content"
                alignItems="center"
                gap="4px"
              >
                <Heading
                  as="h2"
                  data-cy-id="view-name"
                  typography="largeStrong"
                >
                  {name}
                </Heading>
                {canSaveScreener && (
                  <Button
                    styleType="passiveLarge"
                    marginLeft
                    onClick={() => {
                      onEditNameClick && onEditNameClick();
                      trackEvent({
                        action: 'click',
                        subject: 'Edit name and description',
                      });
                    }}
                  >
                    <EditSolid />
                  </Button>
                )}
              </Grid>

              {description && (
                <Box marginTop="x1">
                  <Text typography="small">{description}</Text>
                </Box>
              )}
            </Box>
          ),
        }}
      </If>
    </Box>
  );
};

export default track({
  type: 'track',
  subject: 'header',
})(HeaderExperiment) as typeof HeaderExperiment;
